import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  PoButtonModule,
  PoFieldModule,
  PoSelectOption,
  PoSkeletonModule,
  PoSkeletonVariant
} from '@po-ui/ng-components';

/**
 * Barra de busca, filtro de situação e botão de inclusão, comum às cinco abas.
 *
 * Fica fora do `po-page-list` de propósito: dentro de uma aba ele duplicaria o
 * cromo de página que o container já dá.
 */
@Component({
  selector: 'app-regras-toolbar',
  imports: [CommonModule, FormsModule, PoFieldModule, PoButtonModule, PoSkeletonModule],
  template: `
    <div class="po-row regras-toolbar">
      <po-input
        [class]="classeBusca"
        p-label="Pesquisar"
        [p-placeholder]="placeholder"
        p-icon="an an-magnifying-glass"
        p-clean
        [ngModel]="busca"
        (p-change-model)="buscar.emit($event)"
        name="busca">
      </po-input>

      <po-select
        class="po-md-3"
        p-label="Situação"
        [p-options]="situacaoOptions"
        [ngModel]="filtroAtivo"
        (p-change)="filtrarSituacao.emit($event)"
        name="filtroAtivo">
      </po-select>

      <ng-content select="[extraFiltro]"></ng-content>

      <div class="regras-toolbar-acoes">
        <po-button
          p-label="Atualizar"
          [p-icon]="carregando ? '' : 'an an-arrows-clockwise'"
          p-kind="secondary"
          [p-loading]="carregando"
          (p-click)="atualizar.emit()">
        </po-button>

        <po-button
          [p-label]="rotuloNovo"
          p-icon="an an-plus"
          p-kind="primary"
          (p-click)="novo.emit()">
        </po-button>
      </div>
    </div>
  `,
  styles: [`
    .regras-toolbar { align-items: flex-end; margin-bottom: 0.75rem; }
    /* Alinha o botao pela base dos campos: sem isso ele sobe para o topo da
       linha, porque nao tem label como os campos ao lado. */
    .regras-toolbar-acoes {
      display: flex; justify-content: flex-end; gap: 0.5rem;
      margin-left: auto; padding-bottom: 0.25rem;
    }
    /* O botao vive na view do po-button, fora do encapsulamento deste
       componente - dai o ::ng-deep, ancorado na classe local para nao vazar. */
    .regras-toolbar-acoes ::ng-deep .po-button { white-space: nowrap; }
  `]
})
export class RegrasToolbarComponent {
  @Input() busca = '';
  @Input() filtroAtivo = '';
  @Input() placeholder = 'Pesquisar...';
  @Input() rotuloNovo = 'Novo';
  @Input() situacaoOptions: PoSelectOption[] = [];
  /** Deixa o botao de atualizar em estado de carga enquanto a lista recarrega. */
  @Input() carregando = false;
  /** Largura do campo de busca, para ceder espaço a um filtro extra. */
  @Input() classeBusca = 'po-md-5';

  @Output() buscar = new EventEmitter<string>();
  @Output() filtrarSituacao = new EventEmitter<string>();
  @Output() atualizar = new EventEmitter<void>();
  @Output() novo = new EventEmitter<void>();
}

/** Esqueleto da carga inicial, com a forma da tabela que vem a seguir. */
@Component({
  selector: 'app-regras-skeleton',
  imports: [CommonModule, PoSkeletonModule],
  template: `
    <div class="regras-skeleton" aria-busy="true">
      <po-skeleton [p-variant]="rect" p-width="100%" p-height="40px" [p-aria-label]="rotulo"></po-skeleton>
      <po-skeleton *ngFor="let l of linhas; trackBy: trackLinha" [p-variant]="rect" p-width="100%" p-height="28px"></po-skeleton>
    </div>
  `,
  styles: [`
    .regras-skeleton { display: flex; flex-direction: column; gap: 0.5rem; padding: 0.5rem 0; }
  `]
})
export class RegrasSkeletonComponent {
  @Input() rotulo = 'Carregando registros';
  readonly rect = PoSkeletonVariant.rectangle;

  trackLinha(indice: number): number {
    return indice;
  }
  // Indices distintos: uma lista de 8 `undefined` tem identidades iguais e
  // faz o *ngFor recriar tudo a cada ciclo (Angular NG0956).
  readonly linhas = [0, 1, 2, 3, 4, 5, 6, 7];
}
